/**
 * Email → Person Search pivot context (corporate personal mailboxes).
 *
 * Isolated from Phone Lookup. Mirrors backend ``email_enricher`` heuristics
 * so the UI can render the CTA as soon as domain_intel / deliverability land.
 */

import { isPublicEmailProvider, emailDomainFromAddress } from './emailIntelUi';

const ROLE_LOCAL_PARTS = new Set([
  'admin',
  'administrator',
  'office',
  'contact',
  'info',
  'support',
  'sales',
  'noreply',
  'no-reply',
  'donotreply',
  'do-not-reply',
  'hello',
  'help',
  'billing',
  'hr',
  'jobs',
  'careers',
  'webmaster',
  'postmaster',
  'abuse',
  'security',
  'team',
  'mail',
  'email',
]);

/**
 * Parse personal-style local-part into a display name.
 *
 * @param {unknown} email
 * @returns {string}
 */
export function parsePersonalEmailDisplayName(email) {
  const raw = String(email || '').trim();
  if (!raw.includes('@')) return '';
  const local = raw.split('@')[0].trim().toLowerCase();
  if (!local || ROLE_LOCAL_PARTS.has(local)) return '';
  if (/^[a-z]+\d{2,}$/i.test(local) && !/[._-]/.test(local)) return '';

  const tokenized = local
    .replace(/[0-9]+/g, ' ')
    .replace(/[._+-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const words = tokenized
    .split(' ')
    .map((w) => w.trim())
    .filter((w) => w.length >= 2 && !ROLE_LOCAL_PARTS.has(w));
  if (words.length < 2) return '';
  return words
    .slice(0, 3)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

function cleanOrgLabel(name) {
  let s = String(name || '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!s) return '';
  s = s
    .replace(/\b(s\.?r\.?l\.?|s\.?a\.?|inc\.?|ltd\.?|llc\.?|gmbh|ag|plc|corp\.?|co\.?)\b\.?/gi, '')
    .replace(/[ ,.-]+$/g, '')
    .trim();
  return s.slice(0, 80);
}

/**
 * Prefer org label from domain_intel / cyber_trust rows in the Email feed.
 *
 * @param {object[]} displayResults
 * @param {string} domain
 * @returns {string}
 */
export function preferOrgFromEmailResults(displayResults, domain = '') {
  const list = Array.isArray(displayResults) ? displayResults : [];
  for (const row of list) {
    if (!row || typeof row !== 'object') continue;
    const details = row.details && typeof row.details === 'object' ? row.details : {};
    if (row.source === 'domain_intel' || row.type === 'domain_entity_profile') {
      const legal = Array.isArray(details.legal_names) ? details.legal_names : [];
      const sites = Array.isArray(details.site_names) ? details.site_names : [];
      const preferred = details.org_display_name || details.organization;
      if (preferred) {
        const cleaned = cleanOrgLabel(preferred);
        if (cleaned) return cleaned;
      }
      for (const candidate of [...legal, ...sites]) {
        const cleaned = cleanOrgLabel(candidate);
        if (cleaned && cleaned.length >= 3) return cleaned;
      }
    }
  }
  const host = String(domain || '')
    .trim()
    .toLowerCase()
    .replace(/^www\./, '');
  const root = host.split('.')[0] || '';
  if (root && root.length >= 3 && !['mail', 'email', 'www'].includes(root)) {
    return root.charAt(0).toUpperCase() + root.slice(1);
  }
  return '';
}

/**
 * Prefer location hint from domain_intel site_locations.
 *
 * @param {object[]} displayResults
 * @returns {string}
 */
export function preferLocationFromEmailResults(displayResults) {
  const list = Array.isArray(displayResults) ? displayResults : [];
  for (const row of list) {
    const details = row?.details && typeof row.details === 'object' ? row.details : {};
    const locs = Array.isArray(details.site_locations)
      ? details.site_locations
      : Array.isArray(details.locations)
        ? details.locations
        : [];
    for (const loc of locs) {
      const s = String(loc || '').trim();
      if (s.length >= 2 && s.length <= 64) return s;
    }
    const nested = details.person_pivot;
    if (nested && typeof nested === 'object' && nested.location) {
      const s = String(nested.location).trim();
      if (s) return s;
    }
  }
  return '';
}

/**
 * Build enriched Person pivot context for Email Intelligence UI.
 *
 * @param {unknown} email
 * @param {object[]} [displayResults]
 * @returns {{
 *   eligible: boolean,
 *   personName: string,
 *   companyName: string,
 *   location: string,
 *   domain: string,
 *   label: string,
 *   isWebmail: boolean,
 * }}
 */
export function buildEmailPersonPivotContext(email, displayResults = []) {
  const addr = String(email || '').trim();
  const domain = emailDomainFromAddress(addr);
  const personName = parsePersonalEmailDisplayName(addr);
  const isWebmail = isPublicEmailProvider(domain);
  let companyName = '';
  let location = '';

  // Prefer backend-authored pivot payload when present (deliverability / domain_intel).
  for (const row of displayResults || []) {
    const pivot =
      row?.details?.person_pivot ||
      row?.person_pivot ||
      (row?.ui_bucket === 'email_person_pivot' ? row : null);
    if (!pivot || typeof pivot !== 'object') continue;
    if (pivot.person_name && !personName) {
      // keep local parse as primary
    }
    if (pivot.company_name) companyName = String(pivot.company_name).trim();
    if (pivot.location) location = String(pivot.location).trim();
  }

  if (personName && domain && !isWebmail) {
    if (!companyName) companyName = preferOrgFromEmailResults(displayResults, domain);
    if (!location) location = preferLocationFromEmailResults(displayResults);
  }

  const parts = [companyName, location].filter(Boolean);
  const suffix = parts.length ? ` (${parts.join(', ')})` : '';
  const label = personName ? `Pivot to Person: ${personName}${suffix}` : '';
  const eligible = Boolean(personName) && Boolean(domain) && !isWebmail;

  return {
    eligible,
    personName,
    companyName,
    location,
    domain,
    label,
    isWebmail,
  };
}
